// Ctrl/⌘ K: jump to a page, run an action, or open a category, product, customer or
// invoice. Mounted only while open, so its queries run only then.
import { CommandPalette, type CommandGroup, type Area } from '@ims/ui'
import { FileText, FolderSimple, Package, Plus, Users } from '@ims/ui/icons'
import { useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router'
import { useBusinessMode, useLive, useSettings } from '../hooks.ts'
import { money } from '../lib/format.ts'
import { goShortcut } from '../lib/shortcuts.ts'
import { useCan } from '../workspace.tsx'

export interface PageLink {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
  /** The part of the app the page belongs to: it colours the icon in the sidebar. */
  area?: Area
  /** Other addresses this sidebar item stands for (the tabs of a restaurant's Orders page). */
  also?: readonly string[]
  /** Search only: where a page that became a tab lives now ("Sales → Quotations"). */
  detail?: string
}

export function AppCommandPalette({ pages, onClose }: { pages: PageLink[]; onClose: () => void }) {
  const navigate = useNavigate()
  const settings = useSettings()
  const canEdit = useCan('staff')
  const [query, setQuery] = useState('')
  const term = query.trim()
  const lower = term.toLowerCase()
  const matches = (label: string) => !lower || label.toLowerCase().includes(lower)

  const products = useLive(
    (repo) =>
      term ? repo.products.search(term, { limit: 6, scope: 'catalog' }) : Promise.resolve(null),
    [term],
    ['products'],
  ).data
  // A shop's categories open the Products list filtered by them (a restaurant's Menu has
  // its own sections).
  const restaurant = useBusinessMode().menu
  const categories = useLive(
    async (repo) => {
      if (restaurant) return []
      const [list, summary] = await Promise.all([
        repo.list('categories', { orderBy: { column: 'name' }, limit: 500 }),
        repo.products.summary?.() ?? Promise.resolve(null),
      ])
      const count = new Map(summary?.categories.map((c) => [c.categoryId, c.count]))
      return list.rows.map((c) => ({ ...c, count: count.get(c.id) }))
    },
    [restaurant],
    ['categories', 'products'],
  ).data
  const invoices = useLive(
    (repo) => (term ? repo.invoices.list({ search: term, limit: 5 }) : Promise.resolve(null)),
    [term],
    ['sales_invoices'],
  ).data
  const customers = useLive(
    (repo) => repo.list('customers', { orderBy: { column: 'name' }, limit: 2000 }),
    [],
    ['customers'],
  ).data?.rows

  const actions = [
    { id: 'bill', label: 'New bill', to: '/bill', staff: false },
    ...(pages.some((p) => p.to === '/quotations')
      ? [
          { id: 'quotation', label: 'New quotation', to: '/quotations/new', staff: true },
          { id: 'order', label: 'New order', to: '/orders/new', staff: true },
        ]
      : []),
    { id: 'product', label: 'Add product', to: '/products?new=1', staff: true },
    { id: 'import', label: 'Import products', to: '/products/import', staff: true },
    { id: 'receive', label: 'Receive stock', to: '/stock?receive=1', staff: true },
    { id: 'po', label: 'New purchase order', to: '/purchase-orders/new', staff: true },
    { id: 'customer', label: 'Add customer', to: '/customers?new=1', staff: true },
  ].filter((a) => (!a.staff || canEdit) && matches(a.label))

  const icon = (Icon: ComponentType<{ className?: string }>) => <Icon className="size-4" />
  const groups: CommandGroup[] = [
    {
      label: 'Actions',
      items: actions.map((a) => ({
        id: `a-${a.id}`,
        label: a.label,
        icon: icon(Plus),
        shortcut: a.id === 'bill' ? goShortcut('/bill') : undefined,
        onSelect: () => navigate(a.to),
      })),
    },
    {
      label: 'Categories',
      items: lower
        ? (categories ?? [])
            .filter((c) => c.name.toLowerCase().includes(lower))
            .slice(0, 5)
            .map((c) => ({
              id: `k-${c.id}`,
              label: c.name,
              detail:
                c.count === undefined
                  ? 'Category'
                  : `${c.count} product${c.count === 1 ? '' : 's'}`,
              icon: icon(FolderSimple),
              onSelect: () => navigate(`/products?category=${c.id}`),
            }))
        : [],
    },
    {
      label: 'Products',
      items: (products?.rows ?? []).map((p) => ({
        id: `p-${p.id}`,
        label: p.name,
        detail: [p.sku, money(p.sell_price_paisa, settings)].filter(Boolean).join(' · '),
        icon: icon(Package),
        onSelect: () => navigate(`/products/${p.id}`),
      })),
    },
    {
      label: 'Customers',
      items: lower
        ? (customers ?? [])
            .filter((c) => [c.name, c.phone].some((v) => v?.toLowerCase().includes(lower)))
            .slice(0, 5)
            .map((c) => ({
              id: `c-${c.id}`,
              label: c.name,
              detail: c.phone ?? undefined,
              icon: icon(Users),
              onSelect: () => navigate(`/customers/${c.id}`),
            }))
        : [],
    },
    {
      label: 'Invoices',
      items: (invoices?.rows ?? []).map((i) => ({
        id: `i-${i.id}`,
        label: i.number ?? 'Draft invoice',
        detail: [i.customer_name, money(i.total_paisa, settings)].filter(Boolean).join(' · '),
        icon: icon(FileText),
        onSelect: () => navigate(`/invoices/${i.id}`),
      })),
    },
    {
      label: 'Go to',
      items: pages
        .filter((p) => matches(p.label))
        .map((p) => ({
          // A tab's old name shares its address with the item that holds it now.
          id: `g-${p.to}-${p.label}`,
          label: p.label,
          detail: p.detail,
          icon: icon(p.icon),
          shortcut: p.detail ? undefined : goShortcut(p.to),
          onSelect: () => navigate(p.to),
        })),
    },
  ]

  return (
    <CommandPalette
      open
      onClose={onClose}
      query={query}
      onQuery={setQuery}
      groups={groups}
      placeholder="Search products, customers, invoices or pages…"
    />
  )
}
